import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
	archiveKycFormsTemplate,
	archiveOrganizationKycFormsTemplate,
	createKycFormsTemplates,
	createOrganizationKycFormsTemplates,
	deleteKycFormsTemplates,
	deleteOrganizationKycFormsTemplates,
	getKycFormTemplateInstanceCount,
	getMIPKycFormsTemplates,
	getOrganizationKycFormsTemplates,
	updateKycFormsTemplates,
	updateOrganizationKycFormsTemplates,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	CustomDetailEnum,
	CustomDetailCategory,
	DeleteProgrammeKycFormTemplatesDto,
	DocumentWorkflowTemplateVisibleIn,
} from '@dealstack/shared/lib/generated';
import type { PendingKycFormAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/useTaxAndCompliance.types.ts';
import { formatTemplateForForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/utils/formatTemplateForForm.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { getApiErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { FormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import type { KycPlacementValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';

interface Props {
	mipId: string | undefined;
	organizationId?: string;
	showArchived?: boolean;
}

export const useTaxAndCompliance = ({ mipId, organizationId, showArchived = false }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const {
		data: formsTemplatesResponse,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: organizationId
			? [
					API_PATHS.PROGRAMME_DEFAULTS_TAX_COMPLIANCE_ORGANIZATIONID_GET,
					organizationId,
					showArchived,
				]
			: [API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_GET, mipId, showArchived],
		queryFn: () =>
			organizationId
				? getOrganizationKycFormsTemplates(organizationId, { archived: showArchived })
				: getMIPKycFormsTemplates({ archived: showArchived }),
		enabled: !!mipId || !!organizationId,
	});

	// Editing an organisation's Programme Defaults is an organisation-admin right.
	const { hasPermission } = usePermissionRequest(
		organizationId
			? {
					resourceType: ResourceType.ORGANIZATION,
					resourceId: organizationId,
					permissionRole: PermissionRole.ADMIN,
				}
			: {
					resourceType: ResourceType.PROGRAMME,
					resourceId: defaultToEmptyString(mipId),
					permissionRole: PermissionRole.EDITOR,
				}
	);

	const createFormMutation = useMutation({
		mutationFn: (variables: Parameters<typeof createKycFormsTemplates>[0]) =>
			organizationId
				? createOrganizationKycFormsTemplates(organizationId, variables)
				: createKycFormsTemplates(variables),
	});
	const updateFormMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateKycFormsTemplates>[0]) =>
			organizationId
				? updateOrganizationKycFormsTemplates(organizationId, variables)
				: updateKycFormsTemplates(variables),
	});
	// Organisation defaults are never instantiated, so their endpoints take no applyToInstances.
	const deleteFormMutation = useMutation({
		mutationFn: ({ templateIds, applyToInstances }: DeleteProgrammeKycFormTemplatesDto) =>
			organizationId
				? deleteOrganizationKycFormsTemplates(organizationId, { templateIds })
				: deleteKycFormsTemplates({ templateIds, applyToInstances }),
	});
	const archiveFormMutation = useMutation({
		mutationFn: ({
			templateId,
			archived,
			applyToInstances,
		}: Parameters<typeof archiveKycFormsTemplate>[0]) =>
			organizationId
				? archiveOrganizationKycFormsTemplate(organizationId, { templateId, archived })
				: archiveKycFormsTemplate({ templateId, archived, applyToInstances }),
	});

	const [pendingFormAction, setPendingFormAction] = useState<PendingKycFormAction | null>(null);

	const instanceCountQuery = useQuery({
		queryKey: [
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_TEMPLATEID_INSTANCE_COUNT_GET,
			mipId,
			pendingFormAction?.templateId,
		],
		queryFn: () =>
			getKycFormTemplateInstanceCount(defaultToEmptyString(pendingFormAction?.templateId)),
		enabled: !organizationId && !!pendingFormAction,
		gcTime: 0,
	});

	const allFormsTemplates = formsTemplatesResponse?.payload ?? [];

	const individualFormsTemplates = allFormsTemplates
		.filter((template) =>
			template.visibleIn.includes(DocumentWorkflowTemplateVisibleIn.KYC_INDIVIDUAL)
		)
		.map(formatTemplateForForm);

	const pivFormsTemplates = allFormsTemplates
		.filter((template) =>
			template.visibleIn.includes(DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE)
		)
		.map(formatTemplateForForm);

	const handleAddFormTemplate = async (formTemplate: FormTemplate) => {
		if (!formTemplate.fileId) {
			showToastV2({ title: 'A form must have a file', state: 'error' });
			return;
		}
		await createFormMutation.mutateAsync({
			customDetails: [
				{
					name: formTemplate.name,
					instruction: formTemplate.instruction ?? '',
					type: formTemplate.type,
					fileId: formTemplate.fileId,
					category: formTemplate.category ?? null,
					requiresSignature: formTemplate.requiresSignature ?? false,
					useManualSignaturePlacement: formTemplate.useManualSignaturePlacement ?? false,
					showStampMetadata: formTemplate.showStampMetadata ?? true,
					// The box was placed before the template existed, so it travels with the
					// create call — the backend keys it to the PARTY role it creates.
					pendingSignaturePlacements: formTemplate.pendingPlacement
						? [formTemplate.pendingPlacement]
						: [],
				},
			],
		});

		showToastV2({
			title: tMipCreate('kyc.documents.successfully_added'),
			state: 'success',
		});

		await refetch();
	};

	/**
	 * The template supplies the name and the file; the section supplies the audience and
	 * the category, which the server re-checks against the template's tag.
	 */
	const handleUseTemplate = async (params: {
		globalDocumentTemplateId: string;
		name: string;
		type: CustomDetailEnum;
		category: CustomDetailCategory | null;
	}) => {
		await createFormMutation.mutateAsync({
			customDetails: [
				{
					name: params.name,
					instruction: '',
					type: params.type,
					globalDocumentTemplateId: params.globalDocumentTemplateId,
					category: params.category,
					// Signing is configured on the form afterwards, as it is for an upload.
					requiresSignature: false,
					useManualSignaturePlacement: false,
					showStampMetadata: true,
					pendingSignaturePlacements: [],
				},
			],
		});

		showToastV2({
			title: tMipCreate('kyc.documents.successfully_added'),
			state: 'success',
		});

		await refetch();
	};

	const handleDeleteTemplate = (templateId: string) => {
		setPendingFormAction({ action: 'delete', templateId });
	};

	// Archiving asks first; unarchiving restores every instance, so there is nothing to ask.
	const handleArchiveTemplate = async (templateId: string, archived: boolean) => {
		if (archived) {
			setPendingFormAction({ action: 'archive', templateId });
			return;
		}
		await archiveFormMutation.mutateAsync({
			templateId,
			archived: false,
			applyToInstances: false,
		});
		showToastV2({
			title: tMipCreate('kyc.documents.successfully_unarchived'),
			state: 'success',
		});
		await refetch();
	};

	const handleConfirmFormAction = async (applyToInstances: boolean) => {
		if (!pendingFormAction) {
			return;
		}
		const { action, templateId } = pendingFormAction;
		if (action === 'delete') {
			await deleteFormMutation.mutateAsync({ templateIds: [templateId], applyToInstances });
		} else {
			await archiveFormMutation.mutateAsync({ templateId, archived: true, applyToInstances });
		}
		showToastV2({
			title:
				action === 'delete'
					? tMipCreate('kyc.documents.successfully_deleted')
					: tMipCreate('kyc.documents.successfully_archived'),
			state: 'success',
		});
		setPendingFormAction(null);
		await refetch();
	};

	const handleCancelFormAction = () => {
		setPendingFormAction(null);
	};

	/** False when the save failed, so the drawer stays open rather than showing an unsaved value. */
	const handleUpdateTemplate = async (
		templateId: string,
		updates: {
			name?: string;
			instruction?: string;
			category?: CustomDetailCategory | null;
			requiresSignature?: boolean;
			useManualSignaturePlacement?: boolean;
			showStampMetadata?: boolean;
			pendingPlacement?: KycPlacementValue | null;
		}
	): Promise<boolean> => {
		const template = allFormsTemplates.find((t) => t.id === templateId);

		if (!template) {
			return false;
		}

		try {
			await updateFormMutation.mutateAsync({
				customDetails: [
					{
						templateId,
						name: updates.name ?? template.name,
						instruction: updates.instruction ?? template.instruction ?? '',
						type: template.visibleIn.includes(
							DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
						)
							? CustomDetailEnum.VEHICLE
							: CustomDetailEnum.INDIVIDUAL,
						category:
							updates.category !== undefined ? updates.category : template.category,
						requiresSignature:
							updates.requiresSignature !== undefined
								? updates.requiresSignature
								: template.requiresSignature,
						useManualSignaturePlacement: updates.useManualSignaturePlacement,
						showStampMetadata: updates.showStampMetadata,
						// Enabling signing is what creates the PARTY role, so a box placed in that same
						// drawer session had nothing to key against and the editor could not save it
						// itself. It travels here, and the backend resolves it once the role exists.
						pendingSignaturePlacements: updates.pendingPlacement
							? [updates.pendingPlacement]
							: [],
					},
				],
			});
		} catch (error) {
			// The backend's wording on purpose: a settings lock explains why and what to do instead.
			showToastV2({
				state: 'error',
				message: getApiErrorMessage(error) ?? tMipCreate('kyc.documents.failed_to_save'),
			});
			return false;
		}

		showToastV2({
			title: 'Document updated successfully',
			state: 'success',
		});

		await refetch();
		return true;
	};

	return {
		allFormsTemplates,
		individualFormsTemplates,
		pivFormsTemplates,
		isLoading,
		refetch,
		handleAddFormTemplate,
		handleUseTemplate,
		handleDeleteTemplate,
		handleUpdateTemplate,
		handleArchiveTemplate,
		pendingFormAction,
		instanceCount: instanceCountQuery.data,
		isFormActionPending:
			deleteFormMutation.isPending ||
			archiveFormMutation.isPending ||
			instanceCountQuery.isLoading,
		handleConfirmFormAction,
		handleCancelFormAction,
		createFormMutation,
		deleteFormMutation,
		updateFormMutation,
		hasPermission,
	};
};
